// src/EventForm.js
import { DeleteOutline } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import ImageUploader from "./ImageUploader"; // Import the new component


const REQUIRED_DIMENSIONS = {
    thumbnail: { width: 400, height: 300 },
    web_poster: { width: 1920, height: 1080 },
    mentor_image: { width: 400, height: 400 },
    app_poster: { width: 1080, height: 1920 },
    og_image: { width: 1200, height: 630 },
};

export default function EventForm({ eventTypes, initialData, onCancel, onSubmit }) {

    const [formData, setFormData] = useState({
        ...initialData,
        detailedHeader: initialData?.detailed_description_web
            ? JSON.parse(initialData.detailed_description_web)?.header
            : "",
        bullets: initialData?.detailed_description_web
            ? JSON.parse(initialData.detailed_description_web)?.bullets?.map(b => b.text)
            : [""],
        members: initialData?.detail && !["null", null].includes(initialData?.detail)
            ? JSON.parse(initialData.detail)?.members
            : [{ name: "", image: "", description: "", college: "" }],

        meta_title: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.meta_title
            : "",
        meta_description: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.meta_description
            : "",
        meta_keywords: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.meta_keywords
            : "",

        og_title: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.og_title
            : "",

        og_image: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.og_image
            : "",

        og_description: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.og_description
            : "",

        twitter_title: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.twitter_title
            : "",

        twitter_description: initialData?.meta_data
            ? JSON.parse(initialData.meta_data)?.twitter_description
            : "",

    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setFormData({
            ...initialData,
            signee_details: initialData?.signee_details
                ? Array.isArray(initialData.signee_details) ? initialData.signee_details : JSON.parse(initialData.signee_details)
                : [],
            detailedHeader: initialData?.detailed_description_web
                ? JSON.parse(initialData.detailed_description_web)?.header
                : "",
            bullets: initialData?.detailed_description_web
                ? JSON.parse(initialData.detailed_description_web)?.bullets?.map(b => b.text)
                : [""],
            members: initialData?.detail && !["null", null].includes(initialData?.detail)
                ? JSON.parse(initialData.detail)?.members
                : [{ name: "", image: "", description: "", college: "" }],

            meta_title: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.meta_title
                : "",
            meta_description: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.meta_description
                : "",
            meta_keywords: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.meta_keywords
                : "",

            og_title: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.og_title
                : "",


            og_image: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.og_image
                : "",

            og_description: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.og_description
                : "",

            twitter_title: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.twitter_title
                : "",

            twitter_description: initialData?.meta_data
                ? JSON.parse(initialData.meta_data)?.twitter_description
                : "",
        });
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    // New handler for ImageUploader component to update form state
    const handleImageUrlChange = (name, url) => {
        setFormData(prev => ({
            ...prev,
            [name]: url
        }));

        // Clear error when image is uploaded
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleDateChange = (e) => {
        const localDate = new Date(e.target.value);
        setFormData(prev => ({ ...prev, date: localDate.getTime() }));

        // Clear error when date is selected
        if (errors.date) {
            setErrors(prev => ({
                ...prev,
                date: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Required field validations
        if (!formData.title?.trim()) {
            newErrors.title = "Title is required";
        }

        if (!formData.event_type_id) {
            newErrors.event_type_id = "Event type is required";
        }

        if (!formData.sub_title?.trim()) {
            newErrors.sub_title = "Subtitle is required";
        }

        if (!formData.description?.trim()) {
            newErrors.description = "Description is required";
        }

        if (!formData.about?.trim()) {
            newErrors.about = "About section is required";
        }

        if (!formData.date) {
            newErrors.date = "Date & time is required";
        }

        if (!formData.location?.trim()) {
            newErrors.location = "Location is required";
        }



        if (!formData.thumbnail?.trim()) {
            newErrors.thumbnail = "Thumbnail image is required";
        }

        if (!formData.web_poster?.trim()) {
            newErrors.web_poster = "Web poster image is required";
        }

        // Validate detailed_description_web (bullets)
        const hasValidBullets = formData.bullets.some(bullet => bullet.trim() !== "");
        if (!formData.detailedHeader?.trim() || !hasValidBullets) {
            newErrors.detailed_description_web = "Detailed description web section requires a header and at least one bullet point";
        }

        // is_active is automatically handled by checkbox (0 or 1)

        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});

        const detailed = {
            header: formData.detailedHeader,
            bullets: formData.bullets
                .filter(t => t.trim() !== "")
                .map(text => ({ text }))
        };


        const meta_data = {
            meta_title: formData.meta_title,
            meta_description: formData.meta_description,
            meta_keywords: formData.meta_keywords,
            og_title: formData.og_title,
            og_image: formData.og_image,
            og_description: formData.og_description,
            twitter_title: formData.twitter_title,
            twitter_description: formData.twitter_description,
        };
        const dataToSubmit = {
            ...formData,
            detailed_description_web: JSON.stringify(detailed),
            detail: JSON.stringify({ members: formData.members }),
            meta_data: JSON.stringify(meta_data),
            detailedHeader: undefined,
            bullets: undefined
        };



        try {
            dataToSubmit.fees = parseInt(dataToSubmit.fees, 10) || 0;
            dataToSubmit.duration = parseInt(dataToSubmit.duration, 10) || 0;
            dataToSubmit.event_type_id = parseInt(dataToSubmit.event_type_id, 10) || null;
            onSubmit(dataToSubmit);
        } catch (jsonError) {
            setErrors({ general: "Invalid JSON format in data fields." });
        }
    };

    const dateToLocalString = (timestamp) => {
        if (!timestamp) return "";
        const d = new Date(timestamp);
        const pad = (n) => (n < 10 ? '0' + n : n);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const styles = {
        formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 24px" },
        fullWidth: { gridColumn: "1 / -1" },
        formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
        label: { marginBottom: 0, fontWeight: 600, color: "#444", fontSize: 14 },
        input: { padding: "10px 12px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14, width: '100%', boxSizing: 'border-box' },
        inputError: { padding: "10px 12px", borderRadius: 4, border: "2px solid #dc3545", fontSize: 14, width: '100%', boxSizing: 'border-box' },
        textarea: { padding: "10px 12px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14, minHeight: 100, resize: "vertical", fontFamily: 'inherit' },
        textareaError: { padding: "10px 12px", borderRadius: 4, border: "2px solid #dc3545", fontSize: 14, minHeight: 100, resize: "vertical", fontFamily: 'inherit' },
        checkboxContainer: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: 'pointer' },
        actions: { display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24, paddingTop: 24, borderTop: '1px solid #eee', gridColumn: "1 / -1" },
        button: { padding: "10px 24px", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 16, fontWeight: 600 },
        submitBtn: { background: "#28a745", color: "white" },
        cancelBtn: { background: "#6c757d", color: "white" },
        error: { color: "#dc3545", gridColumn: "1 / -1", textAlign: "center", marginBottom: 10, background: '#ffebee', padding: 10, borderRadius: 4 },
        fieldError: { color: "#dc3545", fontSize: 12, marginTop: 4 },
        sectionHeader: {
            gridColumn: "1 / -1",
            borderBottom: "1px solid #eee",
            paddingBottom: "10px",
            marginBottom: "10px",
            fontSize: "18px",
            color: "#333",
        },
    };


    return (
        <form onSubmit={handleSubmit}>
            {errors.general && <p style={styles.error}>{errors.general}</p>}
            <div style={styles.formGrid}>
                <div style={styles.sectionHeader}>Basic Information</div>
                <div style={styles.formGroup}>
                    <label htmlFor="event_type_id" style={styles.label}>Event Type*</label>
                    <select
                        id="event_type_id"
                        name="event_type_id"
                        value={formData.event_type_id || ''}
                        onChange={handleChange}
                        style={errors.event_type_id ? styles.inputError : styles.input}
                    >
                        <option value="">-- Select Type --</option>
                        {eventTypes.map(type => <option key={type.id} value={type.id}>{type.type}</option>)}
                    </select>
                    {errors.event_type_id && <span style={styles.fieldError}>{errors.event_type_id}</span>}
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="title" style={styles.label}>Title*</label>
                    <input
                        id="title"
                        name="title"
                        value={formData.title || ''}
                        onChange={handleChange}
                        style={errors.title ? styles.inputError : styles.input}
                        required
                    />
                    {errors.title && <span style={styles.fieldError}>{errors.title}</span>}
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="sub_title" style={styles.label}>Subtitle*</label>
                    <input
                        id="sub_title"
                        name="sub_title"
                        value={formData.sub_title || ''}
                        onChange={handleChange}
                        style={errors.sub_title ? styles.inputError : styles.input}
                    />
                    {errors.sub_title && <span style={styles.fieldError}>{errors.sub_title}</span>}
                </div>
                <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                    <label htmlFor="description" style={styles.label}>Short Description (for list view)*</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        style={errors.description ? { ...styles.textareaError, minHeight: '60px' } : { ...styles.textarea, minHeight: '60px' }}
                        maxLength="500"
                    />
                    {errors.description && <span style={styles.fieldError}>{errors.description}</span>}
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="date" style={styles.label}>Date & Time*</label>
                    <input
                        id="date"
                        name="date"
                        type="datetime-local"
                        value={dateToLocalString(formData.date)}
                        onChange={handleDateChange}
                        style={errors.date ? styles.inputError : styles.input}
                    />
                    {errors.date && <span style={styles.fieldError}>{errors.date}</span>}
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="location" style={styles.label}>Location*</label>
                    <input
                        id="location"
                        name="location"
                        value={formData.location || ''}
                        onChange={handleChange}
                        style={errors.location ? styles.inputError : styles.input}
                    />
                    {errors.location && <span style={styles.fieldError}>{errors.location}</span>}
                </div>

                {/* --- MEDIA & POSTERS --- */}
                <div style={styles.sectionHeader}>Media & Posters</div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1", display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                    <div>
                        <ImageUploader
                            label="Thumbnail*"
                            name="thumbnail"
                            value={formData.thumbnail}
                            onUrlChange={handleImageUrlChange}
                            requiredWidth={REQUIRED_DIMENSIONS.thumbnail.width}
                            requiredHeight={REQUIRED_DIMENSIONS.thumbnail.height}
                        />
                        {errors.thumbnail && <span style={styles.fieldError}>{errors.thumbnail}</span>}
                    </div>
                    <div>
                        <ImageUploader
                            label="Web Poster*"
                            name="web_poster"
                            value={formData.web_poster}
                            onUrlChange={handleImageUrlChange}
                            requiredWidth={REQUIRED_DIMENSIONS.web_poster.width}
                            requiredHeight={REQUIRED_DIMENSIONS.web_poster.height}
                        />
                        {errors.web_poster && <span style={styles.fieldError}>{errors.web_poster}</span>}
                    </div>
                    <ImageUploader
                        label="App Poster"
                        name="app_poster"
                        value={formData.app_poster}
                        onUrlChange={handleImageUrlChange}
                        requiredWidth={REQUIRED_DIMENSIONS.app_poster.width}
                        requiredHeight={REQUIRED_DIMENSIONS.app_poster.height}
                    />
                </div>


                {/* --- DETAILS & CONTENT --- */}
                <div style={styles.sectionHeader}>Content & Details</div>
                <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                    <label htmlFor="about" style={styles.label}>About Section*</label>
                    <textarea
                        id="about"
                        name="about"
                        value={formData.about || ''}
                        onChange={handleChange}
                        style={errors.about ? styles.textareaError : styles.textarea}
                        maxLength="5000"
                    />
                    {errors.about && <span style={styles.fieldError}>{errors.about}</span>}
                </div>
                <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                    <label htmlFor="detailed_description" style={styles.label}>Detailed Description (App)</label>
                    <textarea id="detailed_description" name="detailed_description" value={formData.detailed_description || ''} onChange={handleChange} style={styles.textarea} maxLength="2000"></textarea>
                </div>

                <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                    <label htmlFor="detailed_description_web" style={styles.label}>Detailed Description (Web Page Bullets)*</label>
                    <input
                        type="text"
                        name="detailedHeader"
                        value={formData.detailedHeader}
                        onChange={handleChange}
                        placeholder="Section heading (e.g., 'What you will learn')"
                        style={errors.detailed_description_web ? styles.inputError : styles.input}
                    />
                    {formData.bullets.map((text, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <input
                                type="text"
                                name={`bullets[${idx}]`}
                                value={text}
                                onChange={e => {
                                    const newBullets = [...formData.bullets];
                                    newBullets[idx] = e.target.value;
                                    setFormData(f => ({ ...f, bullets: newBullets }));

                                    // Clear error when user starts typing
                                    if (errors.detailed_description_web) {
                                        setErrors(prev => ({
                                            ...prev,
                                            detailed_description_web: ""
                                        }));
                                    }
                                }}
                                placeholder={`Bullet #${idx + 1}`}
                                style={errors.detailed_description_web ? styles.inputError : styles.input}
                            />
                            <button type="button" style={{ background: "none", color: "red", border: "none", cursor: 'pointer' }} onClick={() => {
                                setFormData(f => ({
                                    ...f,
                                    bullets: f.bullets.filter((_, i) => i !== idx)
                                }));
                            }}>
                                <DeleteOutline />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={() => {
                        setFormData(f => ({ ...f, bullets: [...f.bullets, ""] }));
                    }} style={{ marginTop: 12, width: "fit-content", padding: "8px 20px", background: "#1c8ea8", color: "#fff", border: "none", borderRadius: "8px" }}>Add Bullet</button>
                    {errors.detailed_description_web && <span style={styles.fieldError}>{errors.detailed_description_web}</span>}
                </div>

                <div style={styles.sectionHeader}>Speakers / Members</div>
                <div style={styles.formGroup}>
                    <label htmlFor="member_heading" style={styles.label}>Section Heading (e.g., "Our Speakers")</label>
                    <input id="member_heading" name="member_heading" value={formData.member_heading || ''} onChange={handleChange} style={styles.input} />
                </div>
                <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                    {formData.members.map((member, idx) => (
                        <div key={idx} style={{ border: '1px solid #ddd', padding: '16px', borderRadius: '8px', display: "flex", gap: "16px", marginTop: "10px" }}>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: "flex", gap: "16px" }}>
                                    <div style={{ flex: 1, ...styles.formGroup }}>
                                        <label style={styles.label}>Name</label>
                                        <input type="text" value={member.name} onChange={e => {
                                            const members = [...formData.members];
                                            members[idx].name = e.target.value;
                                            setFormData(f => ({ ...f, members }));
                                        }} style={styles.input} />
                                    </div>
                                    <div style={{ flex: 1, ...styles.formGroup }}>
                                        <label style={styles.label}>Upload Image</label>
                                        <ImageUploader
                                            label="Upload Image"
                                            name="mentor_image"
                                            value={member.image}
                                            onUrlChange={(name, url) => {
                                                const members = [...formData.members];
                                                members[idx].image = url;
                                                setFormData(f => ({ ...f, members }));
                                            }}
                                            requiredWidth={REQUIRED_DIMENSIONS.mentor_image.width}
                                            requiredHeight={REQUIRED_DIMENSIONS.mentor_image.height}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: "16px" }}>
                                    <div style={{ flex: 1, ...styles.formGroup }}>
                                        <label style={styles.label}>College/Company</label>
                                        <input type="text" value={member.college} onChange={e => {
                                            const members = [...formData.members];
                                            members[idx].college = e.target.value;
                                            setFormData(f => ({ ...f, members }));
                                        }} style={styles.input} />
                                    </div>
                                    <div style={{ flex: 1, ...styles.formGroup }}>
                                        <label style={styles.label}>Description</label>
                                        <textarea value={member.description} onChange={e => {
                                            const members = [...formData.members];
                                            members[idx].description = e.target.value;
                                            setFormData(f => ({ ...f, members }));
                                        }} style={{ ...styles.textarea, minHeight: '50px' }} />
                                    </div>
                                </div>
                            </div>
                            <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'center' }} onClick={() => setFormData(f => ({
                                ...f,
                                members: f.members.filter((_, i) => i !== idx)
                            }))}>
                                <DeleteOutline sx={{ color: 'red' }} />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setFormData(f => ({ ...f, members: [...f.members, { name: "", image: "", college: "", description: "" }] }))}
                        style={{ marginTop: 12, padding: '10px 20px', background: '#1c8ea8', color: '#fff', border: 'none', borderRadius: 4, width: 'fit-content' }}>
                        Add Member
                    </button>
                </div>


                {/* --- CONFIGURATION --- */}
                <div style={styles.sectionHeader}>Links & Configuration</div>
                <div style={styles.formGroup}>
                    <label htmlFor="join_url" style={styles.label}>Join URL (Zoom, Meet, etc.)</label>
                    <input
                        id="join_url"
                        name="join_url"
                        value={formData.join_url || ''}
                        onChange={handleChange}
                        style={errors.join_url ? styles.inputError : styles.input}
                        placeholder="https://..."
                    />
                    {errors.join_url && <span style={styles.fieldError}>{errors.join_url}</span>}
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="google_form" style={styles.label}>Registration URL (Google Form, etc.)</label>
                    <input id="google_form" name="google_form" value={formData.google_form || ''} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="calendar_link" style={styles.label}>Calander Link</label>
                    <input id="calendar_link" name="calendar_link" value={formData.calendar_link || ''} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="platform_type" style={styles.label}>Platform</label>
                    <input id="platform_type" name="platform_type" value={formData.platform_type || ""} onChange={handleChange} style={styles.input} placeholder="Web" />
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="fees" style={styles.label}>Fees</label>
                    <input id="fees" name="fees" type="number" value={formData.fees || 0} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="duration" style={styles.label}>Duration (in minutes)</label>
                    <input
                        id="duration"
                        name="duration"
                        type="number"
                        value={(formData.duration || 0) / 60000} // Convert ms to minutes for display
                        onChange={(e) =>
                            handleChange({
                                target: {
                                    name: "duration",
                                    value: parseInt(e.target.value) * 60000, // Convert minutes to ms
                                },
                            })
                        }
                        style={styles.input}
                    />
                </div>
                <div style={styles.formGroup}>
                    <label htmlFor="color_code" style={styles.label}>Color Code</label>
                    <input id="color_code" name="color_code" value={formData.color_code || ''} onChange={handleChange} style={styles.input} placeholder="#1D8CF8" />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1", flexDirection: 'row', alignItems: 'center', gap: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <label style={styles.checkboxContainer}>
                        <input id="is_active" name="is_active" type="checkbox" checked={!!formData.is_active} onChange={handleChange} />
                        <span style={{ ...styles.label, marginBottom: 0 }}>Event is Active*</span>
                    </label>
                    <label style={styles.checkboxContainer}>
                        <input id="is_banner" name="is_banner" type="checkbox" checked={!!formData.is_banner} onChange={handleChange} />
                        <span style={{ ...styles.label, marginBottom: 0 }}>Set as Banner</span>
                    </label>
                </div>




                <div style={styles.formGroup}>
                    <label htmlFor="meta_title" style={styles.label}>Meta Title</label>
                    <input
                        id="meta_title"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter meta title"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="meta_description" style={styles.label}>Meta Description</label>
                    <input
                        id="meta_description"
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter meta description"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="meta_keywords" style={styles.label}>Meta Keywords</label>
                    <input
                        id="meta_keywords"
                        name="meta_keywords"
                        value={formData.meta_keywords}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="keyword1, keyword2, keyword3"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="og_title" style={styles.label}>OG Title</label>
                    <input
                        id="og_title"
                        name="og_title"
                        value={formData.og_title}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter Open Graph title"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="og_description" style={styles.label}>OG Description</label>
                    <input
                        id="og_description"
                        name="og_description"
                        value={formData.og_description}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter Open Graph description"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="twitter_title" style={styles.label}>Twitter Title</label>
                    <input
                        id="twitter_title"
                        name="twitter_title"
                        value={formData.twitter_title}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter Twitter card title"
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="twitter_description" style={styles.label}>Twitter Description</label>
                    <input
                        id="twitter_description"
                        name="twitter_description"
                        value={formData.twitter_description}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter Twitter card description"
                    />
                </div>

                <div>
                    <ImageUploader
                        label="OG image*"
                        name="og_image"
                        value={formData.og_image}
                        onUrlChange={handleImageUrlChange}
                        requiredWidth={REQUIRED_DIMENSIONS.og_image.width}
                        requiredHeight={REQUIRED_DIMENSIONS.og_image.height}
                    />
                    {errors.og_image && <span style={styles.fieldError}>{errors.og_image}</span>}
                </div>




                <div style={{ ...styles.formGroup, gridColumn: "1 / -1", flexDirection: 'row', alignItems: 'center', gap: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <div style={styles.sectionHeader}>Certificate Details</div>
                    <label style={styles.checkboxContainer}>
                        <input id="is_certificate_required" name="is_certificate_required" type="checkbox" checked={!!formData.is_certificate_required} onChange={handleChange} />
                        <span style={{ ...styles.label, marginBottom: 0 }}>Certificate required  </span>
                    </label>
                </div>




                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <label htmlFor="prime_header" style={styles.label}>Prime Header</label>
                    <input
                        id="prime_header"
                        name="prime_header"
                        value={formData.prime_header || ''}
                        onChange={handleChange}
                        style={errors.prime_header ? styles.inputError : styles.input}
                        placeholder="Fill whether it's a Certificate/Award/Credential (Ex: Certificate)"
                    />
                    {errors.prime_header && <span style={styles.fieldError}>{errors.prime_header}</span>}
                </div>}
                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <label htmlFor="sub_header" style={styles.label}>Sub Header</label>
                    <input id="sub_header" name="sub_header" value={formData.sub_header || ''} onChange={handleChange} style={styles.input} placeholder="Fill “of participation”" />
                </div>}
                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <label htmlFor="pre_name_phrase" style={styles.label}>Pre Name Phrase</label>
                    <input id="pre_name_phrase" name="pre_name_phrase" value={formData.pre_name_phrase || ''} onChange={handleChange} style={styles.input} placeholder="This {{ Certificate/Award/Credential}} is awared to" />
                </div>}
                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <label htmlFor="post_name_phrase_1" style={styles.label}>Post Name Phrase 1</label>
                    <input id="post_name_phrase_1" name="post_name_phrase_1" value={formData.post_name_phrase_1 || ''} onChange={handleChange} style={styles.input} placeholder="For attending {{Event_Tile}}} webinar/Event " />
                </div>}
                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <label htmlFor="post_name_phrase_2" style={styles.label}>Post Name Phrase 2</label>
                    <input id="post_name_phrase_2" name="post_name_phrase_2" value={formData.post_name_phrase_2 || ''} onChange={handleChange} style={styles.input} placeholder="on Xth/st Month 20XX " />
                </div>}
                {Boolean(formData.is_certificate_required) && <div style={styles.formGroup}>
                    <div>

                    </div>
                </div>}
                {Boolean(formData.is_certificate_required) &&
                    <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                        {Array.isArray(formData.signee_details) && formData.signee_details.length > 0 && formData.signee_details.map((member, idx) => (
                            <div key={idx} style={{ border: '1px solid #ddd', padding: '16px', borderRadius: '8px', display: "flex", gap: "16px", marginTop: "10px" }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ display: "flex", gap: "16px" }}>
                                        <div style={{ flex: 1, ...styles.formGroup }}>
                                            <label style={styles.label}>Name</label>
                                            <input type="text" value={member.name} onChange={e => {
                                                const signee_details = [...formData.signee_details];
                                                signee_details[idx].name = e.target.value;
                                                setFormData(f => ({ ...f, signee_details }));
                                            }} style={styles.input} />
                                        </div>
                                        {/* <div style={{ flex: 1, ...styles.formGroup }}>
                                            <label style={styles.label}>Upload Image</label>
                                            <ImageUploader
                                                label="Upload Image"
                                                name="mentor_image"
                                                value={member.img}
                                                onUrlChange={(name, url) => {
                                                    const signee_details = [...formData.signee_details];
                                                    signee_details[idx].img = url;
                                                    setFormData(f => ({ ...f, signee_details }));
                                                }}
                                                requiredWidth={REQUIRED_DIMENSIONS.mentor_image.width}
                                                requiredHeight={REQUIRED_DIMENSIONS.mentor_image.height}
                                            />
                                        </div> */}
                                        <div style={{ flex: 1, ...styles.formGroup }}>
                                            <label style={styles.label}>Designation</label>
                                            <input type="text" value={member.designation} onChange={e => {
                                                const signee_details = [...formData.signee_details];
                                                signee_details[idx].designation = e.target.value;
                                                setFormData(f => ({ ...f, signee_details }));
                                            }} style={styles.input} />
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "16px" }}>

                                        {/* <div style={{ flex: 1, ...styles.formGroup }}>
                                            <label style={styles.label}>Description</label>
                                            <textarea value={member.description} onChange={e => {
                                                const signee_details = [...formData.signee_details];
                                                signee_details[idx].description = e.target.value;
                                                setFormData(f => ({ ...f, signee_details }));
                                            }} style={{ ...styles.textarea, minHeight: '50px' }} />
                                        </div> */}
                                    </div>
                                </div>
                                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'center' }} onClick={() => setFormData(f => ({
                                    ...f,
                                    signee_details: f.signee_details.filter((_, i) => i !== idx)
                                }))}>
                                    <DeleteOutline sx={{ color: 'red' }} />
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={() => setFormData(f => ({ ...f, signee_details: [...f.signee_details, { name: "", img: "", designation: "" }] }))}
                            style={{ marginTop: 12, padding: '10px 20px', background: '#1c8ea8', color: '#fff', border: 'none', borderRadius: 4, width: 'fit-content' }}>
                            Add Signee
                        </button>
                    </div>

                }







                <div style={styles.actions}>
                    <button type="button" onClick={onCancel} style={{ ...styles.button, ...styles.cancelBtn }}>Cancel</button>
                    <button type="submit" style={{ ...styles.button, ...styles.submitBtn }}>Save Changes</button>
                </div>
            </div>
        </form>
    );
}